import { Injectable, inject, RESPONSE_INIT } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry, timeout } from 'rxjs/operators';

import { STRAPI_CONFIG } from '../api/strapi.config';
import { environment } from '../../../environments/environment';
import { toStrapiParams } from '../utils/strapi-params.util';
import { stripHiddenSections } from '../utils/strip-hidden-sections.util';
import { getPopulatePreset } from '../presets/populate-presets';
import { StrapiResponse, StrapiCollectionResponse } from '../models/strapi.models';
import { AssetType } from '../models/asset-type.models';
import { IndustryPage } from '../models/industry.models';
import { ArchivePage, ArchivePageFull } from '../models/archive-page.models';
import { InsightPage, NewsPage } from '../models/article-page.models';
import { EventPage } from '../models/event-page.models';
import { WebinarPage } from '../models/webinar-page.models';
import { CaseStudyPage } from '../models/case-study.models';
import { StaticPage } from '../models/static-page.models';
import { LandingPage } from '../models/landing-page.models';
import { LeadMagnetPage } from '../models/lead-magnet-page.models';
import { Partner } from '../models/partner.models';
import { Global } from '../models/global.models';
import { Navigation } from '../models/navigation.models';
import { SchemaConstants } from '../models/schema-constants.models';
import { ResolveResult } from '../models/resolve.models';
import { FaqEntry } from '../models/faq.models';

@Injectable({ providedIn: 'root' })
export class StrapiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(STRAPI_CONFIG);
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });

  /**
   * StrapiService is draft-mode agnostic. When the incoming SSR request
   * carries a valid __samotics_preview cookie, strapiAuthInterceptor
   * (core/api/strapi-auth.interceptor.ts) appends ?status=draft to every
   * outgoing Strapi URL. The bearer token is unchanged — in Strapi v5 the
   * same read-only token reads both published and draft content, so the
   * distinction lives entirely at the URL query-param layer. Service
   * methods themselves remain unchanged and continue to build the same
   * URL/body they built in v1.6.
   *
   * This means every method below — findOne, findBySlug, findMany,
   * findByCustomSlug, resolve, findNavigation, findGlobal, getFaqEntries,
   * and every typed convenience — automatically participates in preview.
   * There is no opt-in / opt-out per CPT.
   */

  // ---------------------------------------------------------------------------
  // Generic methods
  // ---------------------------------------------------------------------------

  /**
   * Fetch a single document by its documentId.
   */
  findOne<T>(
    contentType: string,
    documentId: string,
    preset: string,
  ): Observable<StrapiResponse<T>> {
    const populate = getPopulatePreset(contentType, preset);
    const params = toStrapiParams({ populate });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/${contentType}/${documentId}?${params}`;

    return this.http.get<StrapiResponse<T>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      // spec 33: strip hidden sections BEFORE the response enters Angular's
      // HTTP transfer cache (captured by provideClientHydration), otherwise
      // hrefs inside hidden sections leak into the SSR hydration JSON.
      map((res) => ({ ...res, data: stripHiddenSections(res.data) })),
      catchError((err: HttpErrorResponse) => this.handleError(err, contentType, documentId)),
    );
  }

  /**
   * Fetch a single document by slug. Queries collection endpoint with
   * a filter, then extracts the first result.
   *
   * Retry is placed BEFORE the map so that transport/5xx errors are
   * retried but an empty result set (slug not found) is NOT retried.
   */
  findBySlug<T>(
    contentType: string,
    slug: string,
    preset: string,
  ): Observable<StrapiResponse<T>> {
    const populate = getPopulatePreset(contentType, preset);
    const params = toStrapiParams({
      filters: { slug: { $eq: slug } },
      populate,
    });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/${contentType}?${params}`;

    return this.http.get<StrapiCollectionResponse<T>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      map((res) => {
        if (!res.data.length) {
          throw new HttpErrorResponse({ status: 404, statusText: 'Not Found', url });
        }
        // spec 33: strip hidden sections before the transfer cache captures
        // the unwrapped entry (see findOne for full rationale).
        return {
          data: stripHiddenSections(res.data[0]),
          meta: res.meta,
        } as StrapiResponse<T>;
      }),
      catchError((err) => this.handleError(err as HttpErrorResponse, contentType, slug)),
    );
  }

  /**
   * Fetch a paginated collection.
   */
  findMany<T>(
    contentType: string,
    preset: string,
    options?: { page?: number; pageSize?: number; sort?: string | string[] },
  ): Observable<StrapiCollectionResponse<T>> {
    const populate = getPopulatePreset(contentType, preset);
    const params = toStrapiParams({
      populate,
      pagination: {
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 25,
      },
      // Phase 44 — multi-key sort encoded via toStrapiParams' array branch
      // (sort[0]=…&sort[1]=…). Single string callers pass through unchanged
      // (wrapped to a single-element array, same wire output as pre-v1.9).
      sort: options?.sort
        ? (Array.isArray(options.sort) ? options.sort : [options.sort])
        : undefined,
    });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/${contentType}?${params}`;

    return this.http.get<StrapiCollectionResponse<T>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, contentType)),
    );
  }

  // ---------------------------------------------------------------------------
  // Custom slug endpoints — server-side deep populate, no query params
  // ---------------------------------------------------------------------------

  /**
   * Fetch a single document via Strapi custom `/by-slug/:slug` endpoint.
   * The server handles full 3-level deep populate, avoiding URL length limits.
   */
  findByCustomSlug<T>(
    contentType: string,
    slug: string,
  ): Observable<StrapiResponse<T>> {
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/${contentType}/by-slug/${encodeURIComponent(slug)}`;

    return this.http.get<StrapiResponse<T>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      // spec 33: hot path for static-page / landing-page / partner /
      // case-study / asset-type / industry / archive / event — strip hidden
      // sections before the transfer cache captures the payload.
      map((res) => ({ ...res, data: stripHiddenSections(res.data) })),
      catchError((err: HttpErrorResponse) => this.handleError(err, contentType, slug)),
    );
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Asset Types
  // ---------------------------------------------------------------------------

  findAssetTypeBySlug(slug: string): Observable<StrapiResponse<AssetType>> {
    return this.findByCustomSlug<AssetType>('asset-type-pages', slug);
  }

  findAssetTypes(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<AssetType>> {
    return this.findMany<AssetType>('asset-type-pages', 'list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Industry Pages
  // ---------------------------------------------------------------------------

  findIndustryBySlug(slug: string): Observable<StrapiResponse<IndustryPage>> {
    return this.findByCustomSlug<IndustryPage>('industry-pages', slug);
  }

  findIndustries(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<IndustryPage>> {
    return this.findMany<IndustryPage>('industry-pages', 'list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Insight Pages
  // ---------------------------------------------------------------------------

  findInsightBySlug(slug: string): Observable<StrapiResponse<InsightPage>> {
    return this.findByCustomSlug<InsightPage>('insight-pages', slug);
  }

  findInsights(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<InsightPage>> {
    return this.findMany<InsightPage>('insight-pages', 'list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — News Pages
  // ---------------------------------------------------------------------------

  findNewsBySlug(slug: string): Observable<StrapiResponse<NewsPage>> {
    return this.findByCustomSlug<NewsPage>('news-pages', slug);
  }

  findNews(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<NewsPage>> {
    return this.findMany<NewsPage>('news-pages', 'list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Case Studies
  // ---------------------------------------------------------------------------

  findCaseStudyBySlug(slug: string): Observable<StrapiResponse<CaseStudyPage>> {
    return this.findByCustomSlug<CaseStudyPage>('case-studies', slug);
  }

  findCaseStudies(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<CaseStudyPage>> {
    return this.findMany<CaseStudyPage>('case-studies', 'list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Static Pages
  // ---------------------------------------------------------------------------

  /**
   * Static pages use the custom `/by-slug/:slug` endpoint — backend handles
   * full deep populate of the `sections` Dynamic Zone server-side, same as
   * every other CPT. No client-side populate query needed.
   */
  findStaticPageBySlug(slug: string): Observable<StrapiResponse<StaticPage>> {
    return this.findByCustomSlug<StaticPage>('static-pages', slug);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Landing Pages
  // ---------------------------------------------------------------------------

  /**
   * Landing pages use the custom `/landing-pages/by-slug/:slug` endpoint —
   * backend handles full deep populate of the `sections` Dynamic Zone
   * server-side, same as static-pages / insight-pages / event-pages etc.
   * (STATE decision 22-03 / 31-03: any DZ-only CPT needs the custom by-slug
   * controller so deep block trees never hit 431 URI length.)
   */
  findLandingPageBySlug(slug: string): Observable<StrapiResponse<LandingPage>> {
    return this.findByCustomSlug<LandingPage>('landing-pages', slug);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Partners
  // ---------------------------------------------------------------------------

  findPartnerBySlug(slug: string): Observable<StrapiResponse<Partner>> {
    return this.findByCustomSlug<Partner>('partners', slug);
  }

  findPartners(
    options?: { page?: number; pageSize?: number },
  ): Observable<StrapiCollectionResponse<Partner>> {
    return this.findMany<Partner>('partners', 'archive-list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Event Pages
  // ---------------------------------------------------------------------------

  findEventBySlug(slug: string): Observable<StrapiResponse<EventPage>> {
    return this.findByCustomSlug<EventPage>('event-pages', slug);
  }

  findEvents(
    options?: { page?: number; pageSize?: number; sort?: string | string[] },
  ): Observable<StrapiCollectionResponse<EventPage>> {
    return this.findMany<EventPage>('event-pages', 'archive-list', options);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Webinar Pages
  // ---------------------------------------------------------------------------

  /**
   * Published webinar fetch by slug. Maps to GET /api/webinar-pages/by-slug/<slug>
   * (custom backend controller — server-side deep populate; cannot use
   * filter-based ?filters[slug][$eq]=...&populate=* due to URI length on deep
   * block trees, per STATE 22-03/31-03 decision).
   *
   * Under preview cookie, strapiAuthInterceptor rewrites the URL to
   * ?status=draft and swaps the bearer to STRAPI_PREVIEW_TOKEN — path-blind
   * by design (Pitfall 32 mechanics).
   */
  getWebinarPageBySlug(slug: string): Observable<StrapiResponse<WebinarPage>> {
    return this.findByCustomSlug<WebinarPage>('webinar-pages', slug);
  }

  /**
   * Preview webinar fetch by Strapi documentId. Maps to
   * GET /api/webinar-pages/by-id/<documentId>. Used by editors clicking
   * "Open preview" in admin (per Phase 37 preview wiring).
   *
   * Note: there is no generic `findByCustomDocId` helper on this service.
   * This method uses the SAME direct `http.get` pattern as `findArchivePageById`
   * (line ~412): timeout(10_000) + retry({count:1, delay:1_000}) +
   * handleError(err, 'webinar-pages', documentId).
   */
  getWebinarPageById(documentId: string): Observable<StrapiResponse<WebinarPage>> {
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/webinar-pages/by-id/${encodeURIComponent(documentId)}`;
    return this.http.get<StrapiResponse<WebinarPage>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'webinar-pages', documentId)),
    );
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Lead Magnet Pages
  // ---------------------------------------------------------------------------

  /**
   * Lead magnet pages (spec 34) use the custom `/lead-magnet-pages/by-slug/:slug`
   * endpoint — backend handles full deep populate server-side (same DZ-only
   * pattern as static-page / landing-page / event-page).
   *
   * findByCustomSlug already routes the response through stripHiddenSections
   * (spec 33, quick-68) so hidden-section hrefs never leak into the transfer
   * cache. No preview-mode branching needed — strapiAuthInterceptor handles
   * ?status=draft at the URL layer.
   */
  findLeadMagnetPageBySlug(slug: string): Observable<StrapiResponse<LeadMagnetPage>> {
    return this.findByCustomSlug<LeadMagnetPage>('lead-magnet-pages', slug);
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Global
  // ---------------------------------------------------------------------------

  findGlobal(): Observable<StrapiResponse<Global>> {
    const params = toStrapiParams({ populate: ['favicon'] });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/global?${params}`;

    return this.http.get<StrapiResponse<Global>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'global')),
    );
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Navigation (header/footer/CTA/robots single type)
  // ---------------------------------------------------------------------------

  /**
   * Fetch the `navigation` single type powering header, footer, CTAs, and
   * robots.txt. Uses nested populate so `headerNav` items expose both `links`
   * and `groups[].items`, and `footerColumns` expose their `links`.
   *
   * The hand-built query string mirrors the shape in
   * `.guillaume/specs/strapi-to-frontend/25-navigation-single-type.md` exactly
   * (`toStrapiParams` cannot express the mixed array+object nested populate
   * shape Strapi expects here).
   */
  findNavigation(): Observable<StrapiResponse<Navigation>> {
    const qs =
      'populate[headerNav][populate][links]=true' +
      '&populate[headerNav][populate][groups][populate]=*' +
      '&populate[footerColumns][populate]=*';
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/navigation?${qs}`;

    return this.http.get<StrapiResponse<Navigation>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'navigation')),
    );
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Schema Constants (single-type, JSON-LD source)
  // ---------------------------------------------------------------------------

  /**
   * Fetch the `schema-constants` single-type used to build the full
   * Organization JSON-LD node on `/about`. Shallow populate is insufficient —
   * `address` is a Strapi component and `contactPoints` is a repeatable
   * component, so `populate=*` is required. The cached `SchemaConstantsService`
   * wraps this call with `shareReplay` so `/about` pays the cost once.
   */
  findSchemaConstants(): Observable<StrapiResponse<SchemaConstants>> {
    const params = toStrapiParams({ populate: '*' });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/schema-constants?${params}`;
    return this.http.get<StrapiResponse<SchemaConstants>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'schema-constants')),
    );
  }

  // ---------------------------------------------------------------------------
  // CMS Route Resolution
  // ---------------------------------------------------------------------------

  resolve(path: string): Observable<ResolveResult> {
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/resolve?path=${encodeURIComponent(path)}`;
    return this.http.get<ResolveResult>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      // spec 33: ResolveResult is a discriminated union returned at the top
      // level of the HTTP body (NOT wrapped under `.data`). The redirect
      // branch has no section-bearing fields; the CMS branch has no
      // sections/extraSections/body on the `CmsResolveResult` interface
      // itself (those live on the prefetchedEntry the resolver may attach
      // later). stripHiddenSections is a no-op on objects without section
      // fields, so applying it to the CMS branch is defence-in-depth for
      // any future inlining. The redirect branch is explicitly skipped so
      // its shape is preserved byte-for-byte.
      map((res) =>
        res && (res as { type?: string }).type === 'redirect'
          ? res
          : stripHiddenSections(res),
      ),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'resolve', path)),
    );
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — Archive Pages
  // ---------------------------------------------------------------------------

  findArchivePageBySlug(slug: string): Observable<StrapiResponse<ArchivePageFull>> {
    return this.findByCustomSlug<ArchivePageFull>('archive-pages', slug);
  }

  findArchivePageById(documentId: string): Observable<StrapiResponse<ArchivePageFull>> {
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/archive-pages/by-id/${encodeURIComponent(documentId)}`;
    return this.http.get<StrapiResponse<ArchivePageFull>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'archive-pages', documentId)),
    );
  }

  findArchivePageByContentType(
    contentType: string,
  ): Observable<StrapiResponse<ArchivePage>> {
    const populate = getPopulatePreset('archive-pages', 'breadcrumb');
    const params = toStrapiParams({
      filters: { contentType: { $eq: contentType } },
      populate,
    });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/archive-pages?${params}`;

    return this.http.get<StrapiCollectionResponse<ArchivePage>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      map((res) => {
        if (!res.data.length) {
          throw new HttpErrorResponse({ status: 404, statusText: 'Not Found', url });
        }
        return { data: res.data[0], meta: res.meta } as StrapiResponse<ArchivePage>;
      }),
      catchError((err) => this.handleError(err as HttpErrorResponse, 'archive-pages', contentType)),
    );
  }

  findArchivePages(): Observable<StrapiCollectionResponse<ArchivePage>> {
    return this.findMany<ArchivePage>('archive-pages', 'breadcrumb');
  }

  // ---------------------------------------------------------------------------
  // Typed convenience methods — FAQ Entries
  // ---------------------------------------------------------------------------

  /**
   * FAQP-02 — Fetch every FAQ entry with its category populated, sorted by
   * `category.sortOrder` asc then `sortOrder` asc. pageSize 100 comfortably
   * returns the full bounded corpus in a single request.
   *
   * Direct `http.get` (mirrors `findArchivePageById`) because `findMany` does
   * not accept a `populate` option and `faq-entries` has no POPULATE_PRESETS
   * entry — the populate string is baked into this URL only.
   */
  getFaqEntries(): Observable<StrapiCollectionResponse<FaqEntry>> {
    const url =
      `${this.config.baseUrl}${this.config.apiPrefix}/faq-entries` +
      `?status=published` +
      `&populate=category` +
      `&sort=category.sortOrder:asc,sortOrder:asc` +
      `&pagination[pageSize]=100`;
    return this.http.get<StrapiCollectionResponse<FaqEntry>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, 'faq-entries')),
    );
  }

  // ---------------------------------------------------------------------------
  // Generic filter method
  // ---------------------------------------------------------------------------

  findByFilters<T>(
    contentType: string,
    filters: Record<string, unknown>,
    preset: string,
  ): Observable<StrapiCollectionResponse<T>> {
    const populate = getPopulatePreset(contentType, preset);
    const params = toStrapiParams({ filters, populate });
    const url = `${this.config.baseUrl}${this.config.apiPrefix}/${contentType}?${params}`;

    return this.http.get<StrapiCollectionResponse<T>>(url).pipe(
      timeout(10_000),
      retry({ count: 1, delay: 1_000 }),
      catchError((err: HttpErrorResponse) => this.handleError(err, contentType)),
    );
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  private handleError(
    error: HttpErrorResponse,
    contentType: string,
    identifier?: string,
  ): Observable<never> {
    const context = identifier ? `${contentType}/${identifier}` : contentType;

    // Page-level fallbacks may turn this error into a 404. Preserve the
    // upstream failure for the SSR response boundary, which returns 503
    // without the 404 body or its noindex tag. Actual missing entries stay 404.
    if (this.responseInit && error.status !== 404) {
      const headers = new Headers(this.responseInit.headers);
      headers.set('X-Samotics-Cms-Unavailable', '1');
      headers.set('Cache-Control', 'no-store');
      this.responseInit.headers = headers;
    }

    if (environment.production) {
      // Prod: log status + context only — never leak full backend URLs or
      // raw HttpErrorResponse messages (which can include query params or
      // upstream error payloads) to the browser console.
      console.error(
        `[STRAPI_ERROR] ${error.status} ${error.statusText} - ${context}`,
      );
    } else {
      // Dev/non-prod: keep the full payload to aid debugging.
      console.error(
        `[STRAPI_ERROR] ${error.status} ${error.statusText} - ${context}`,
        { url: error.url, status: error.status, message: error.message },
      );
    }

    // Surface 401/403 as a distinct [STRAPI_AUTH] warning in dev/preview
    // (suppressed in prod where credentials shouldn't reach the browser).
    // Closes the silent-token-scope-drift class of bug surfaced in
    // quick-83: when a new Strapi CT is added, existing API tokens don't
    // auto-grant find/findOne, the SSR call 401s, the resolver's hard-404
    // contract turns it into HTTP 404, and the actual cause is invisible
    // unless someone curls. Now the function-log line names the CT.
    if (!environment.production && (error.status === 401 || error.status === 403)) {
      console.warn(
        `[STRAPI_AUTH] ${error.status} on ${context} — token may lack ${contentType}::find / findOne permission. Check Strapi admin → Settings → API Tokens.`,
      );
    }

    return throwError(() => error);
  }
}
